import { useEffect, useState } from "react";
import useSWR from "swr";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { SpineAvatar } from "AdminPanel/SpineAvatars/types";
import { AvatarMediaPreview } from "AdminPanel/SpineAvatars/SpineAvatars/components/AvatarMediaPreview";
import MyPagination from "components/ui/MyPagination";
import MyRefetchButton from "components/ui/MyRefetchButton";

export interface SpineAvatarPickerSelection {
  id: string;
  url?: string;
  videoUrl?: string;
  videoPreviewUrl?: string;
}

/** 置顶展示的当前已选项（如「当前 Cover」「当前 Preview」） */
export interface SpineAvatarPickerPinned {
  id?: string;
  url?: string;
  videoUrl?: string;
  videoPreviewUrl?: string;
  name?: string;
  desc?: string;
  badge: string;
}

type PickerSelection = {
  id?: string;
  url?: string;
  videoUrl?: string;
  videoPreviewUrl?: string;
  name?: string;
  desc?: string;
};

const PAGE_SIZE = 40;

/** Cover / Preview 需要静态图 url；无 url 视为生图未完成，不可选用 */
const hasAvatarImage = (avatar?: PickerSelection | null) =>
  Boolean(avatar?.url);

interface SpineAvatarPickerProps {
  tid: string;
  currentAvatarId?: string | null;
  /** 置顶展示的当前已选 Cover / Preview */
  pinnedItems?: SpineAvatarPickerPinned[];
  children: React.ReactNode;
  onSelect: (avatar: SpineAvatarPickerSelection) => void;
}

export function SpineAvatarPicker({
  tid,
  currentAvatarId,
  pinnedItems = [],
  children,
  onSelect,
}: SpineAvatarPickerProps) {
  const [selected, setSelected] = useState<PickerSelection | null>(
    currentAvatarId ? { id: currentAvatarId } : null
  );
  const [open, setOpen] = useState(false);
  const [after, setAfter] = useState<string | null>(null);
  const [afters, setAfters] = useState<string[]>([]);

  const {
    data: avatars = [],
    isLoading,
    mutate: mutateList,
  } = useSWR<SpineAvatar[]>(
    tid && open
      ? [
          "/spine-avatars",
          {
            tid,
            clientVer: "0",
            state: 0,
            limit: PAGE_SIZE,
            ...(after ? { after } : {}),
          },
        ]
      : null
  );

  const keyOf = (a?: PickerSelection | null) => a?.id || a?.url;
  const isSelected = (a: PickerSelection) =>
    !!keyOf(selected) && keyOf(selected) === keyOf(a);

  // 打开时 selected 只有 id；等列表/置顶数据回来后补全 url，已有图的可直接保存
  useEffect(() => {
    if (!open || !selected?.id || hasAvatarImage(selected)) return;
    const found = [...pinnedItems, ...avatars].find(
      (a) => a.id === selected.id && hasAvatarImage(a)
    );
    if (found) setSelected(found);
  }, [open, selected, pinnedItems, avatars]);

  const nextAfter =
    avatars.length === PAGE_SIZE ? avatars[avatars.length - 1]?.id : null;

  const goAfter = (next: string | null) => {
    setAfter(next);
    if (next === null) {
      setAfters([]);
    } else {
      const index = afters.indexOf(next);
      if (index === -1) setAfters([...afters, next]);
    }
  };

  const handleSelectAvatar = (avatar: PickerSelection) => {
    if (!hasAvatarImage(avatar)) return;
    setSelected(avatar);
  };

  const handleSubmit = () => {
    if (!hasAvatarImage(selected)) return;
    onSelect({
      id: selected!.id ?? "",
      url: selected!.url,
      videoUrl: selected!.videoUrl,
      videoPreviewUrl: selected!.videoPreviewUrl,
    });
    handleOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setAfter(null);
      setAfters([]);
      setSelected(currentAvatarId ? { id: currentAvatarId } : null);
    }
  };

  const handleRefresh = async () => {
    await mutateList();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>选择 Spine Avatar</DialogTitle>
          <DialogDescription>
            图片未生成完成的不能用作 Cover / Preview，可点刷新查看最新状态。
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-4">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
            className="m-0 w-auto"
          />
          <MyRefetchButton onClick={handleRefresh} />
        </div>

        <ScrollArea className="flex-1 max-h-[50vh]">
          {isLoading && pinnedItems.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              加载中...
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 p-2">
              {pinnedItems.map((item) => (
                <SpineAvatarItem
                  key={`pinned-${item.badge}`}
                  avatar={item}
                  badge={item.badge}
                  selected={isSelected(item)}
                  onClick={() => handleSelectAvatar(item)}
                />
              ))}
              {avatars.map((avatar: SpineAvatar) => (
                <SpineAvatarItem
                  key={avatar.id}
                  avatar={avatar}
                  selected={isSelected(avatar)}
                  onClick={() => handleSelectAvatar(avatar)}
                />
              ))}
              {!isLoading &&
                avatars.length === 0 &&
                pinnedItems.length === 0 && (
                  <div className="col-span-2 flex items-center justify-center h-32 text-muted-foreground">
                    暂无数据
                  </div>
                )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={!hasAvatarImage(selected)}>
            确定
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SpineAvatarItemProps {
  avatar: PickerSelection;
  selected: boolean;
  badge?: string;
  onClick: () => void;
}

function SpineAvatarItem({
  avatar,
  selected,
  badge,
  onClick,
}: SpineAvatarItemProps) {
  const previewVideoUrl = avatar.videoUrl || avatar.videoPreviewUrl;
  const ready = hasAvatarImage(avatar);
  const hasMedia = ready || Boolean(previewVideoUrl);

  return (
    <div
      className={cn(
        "relative flex flex-col gap-1 p-2 rounded-lg border-2 cursor-pointer transition-all",
        "hover:border-primary hover:shadow-md",
        selected
          ? "border-primary bg-primary/10 ring-2 ring-primary/30"
          : "border-transparent bg-muted/30",
        !ready && "opacity-60"
      )}
      onClick={onClick}
    >
      {badge && (
        <div className="absolute top-1 left-1 z-10 rounded bg-primary/90 px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
          {badge}
        </div>
      )}
      {!ready && (
        <div className="absolute top-1 right-1 z-10 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          生成中
        </div>
      )}
      {hasMedia ? (
        <div className="relative aspect-square w-full overflow-hidden rounded-md border bg-black">
          <AvatarMediaPreview
            videoUrl={previewVideoUrl}
            imageUrl={avatar.url}
            lazyVideo={false}
            className="h-full w-full"
          />
        </div>
      ) : (
        <div className="aspect-square w-full rounded-md border bg-muted flex items-center justify-center">
          <span className="text-[10px] text-muted-foreground">生成中</span>
        </div>
      )}
      <div className="min-w-0">
        <div className="text-xs font-medium truncate">
          {avatar.name?.trim() || "-"}
        </div>
        {avatar.id && (
          <div className="text-[10px] text-muted-foreground truncate">
            ID: {avatar.id}
          </div>
        )}
      </div>
    </div>
  );
}
